/**
 * The reader (prototype `readPane`).
 *
 * The command bar changes with the folder: a draft is continued or discarded, everything
 * else can be replied to, forwarded, flagged, archived or deleted. Customer mail additionally
 * offers "Build RFQ"; supplier mail offers "Parse quote" — the same message routed to
 * whichever screen handles that side of the conversation.
 */

import { Badge } from '../../components/Badge'
import { Button, Placeholder } from '../../components/ui'
import { AttachmentCard } from './AttachmentPreview'
import { CreateEnquiryBar } from './CreateEnquiryBar'
import type { EnquirySummary } from '../../lib/types'
import { ClassificationBar } from './ClassificationBar'
import { readsAsSupplierReply } from '../../lib/mailVerdictOverride'
import { formatDate, formatTime, initials } from '../../lib/format'
import { avatarColor, displaySender, htmlToText, isCustomerMail, isInboundFolder } from '../../lib/mail'
import type { MailAttachment, MailFolder, MailMessage } from '../../lib/types'

export function ReaderPane({
  mail,
  folder,
  isPending,
  onReply,
  onReplyAll,
  onForward,
  onEditDraft,
  onToggleFlag,
  onDelete,
  onOpenEnquiry,
  onPreviewAttachment,
  alreadyProcessed = [],
}: {
  mail: MailMessage | null
  /** Enquiries already created from this mail — see `CreateEnquiryBar`'s own prop. */
  alreadyProcessed?: readonly EnquirySummary[]
  folder: MailFolder
  isPending: boolean
  onReply: () => void
  onReplyAll: () => void
  onForward: () => void
  onEditDraft: () => void
  onToggleFlag: () => void
  onDelete: () => void
  onOpenEnquiry: (enquiryId: string) => void
  onPreviewAttachment: (attachment: MailAttachment) => void
}) {
  if (isPending) {
    return (
      <div className="readpane">
        <Placeholder>Opening…</Placeholder>
      </div>
    )
  }

  if (!mail) {
    return (
      <div className="readpane">
        <div className="pad" style={{ color: 'var(--muted)' }}>
          Select a message.
        </div>
      </div>
    )
  }

  const isDraft = folder === 'drafts'
  // Folder-based, not account-based: every message has an account, so testing it is always true.
  const isInbound = isInboundFolder(folder)
  const enquiryId = mail.enquiry_id
  // Same identity the list row now shows (`lib/mail.ts`'s `displaySender`) — every message in
  // this mailbox arrives forwarded by the same internal address, so showing that address here
  // while the list row shows the real customer would just move the "who is this actually from"
  // confusion from the list into the reader instead of fixing it.
  const sender = displaySender(mail)

  return (
    <div className="readpane">
      <div className="rcmd">
        {isDraft ? (
          <>
            <Button variant="primary" onClick={onEditDraft}>
              ✏️ Continue editing
            </Button>
            <Button variant="danger" onClick={onDelete}>
              🗑️ Delete
            </Button>
          </>
        ) : (
          <>
            <Button variant="primary" onClick={onReply}>
              ↩ Reply
            </Button>
            <Button onClick={onReplyAll}>↩ Reply all</Button>
            <Button onClick={onForward}>➤ Forward</Button>
            {/*
              Only shown when the mail already RESOLVES to an enquiry, in which case this opens it.
              The "no enquiry yet" branch lives in `CreateEnquiryBar`, below the body — confirming
              the guessed customer/location means reading the mail, and a command-bar button cannot
              show that confirmation without covering the mail it's about.
            */}
            {isInbound && isCustomerMail(mail) && enquiryId ? (
              <Button onClick={() => onOpenEnquiry(enquiryId)}>⚙️ Build RFQ</Button>
            ) : null}
            {isInbound && !isCustomerMail(mail) && enquiryId ? (
              <Button onClick={() => onOpenEnquiry(enquiryId)}>📥 Parse quote</Button>
            ) : null}
            {/*
              `mail.flagged` reads `false` for every message now — `get_emails`/`get_email` carry
              no flag field to read back (see `useMail.ts`'s `toMailListItem`), so this can only
              ever offer "Flag", never truthfully show "Unflag". Archive is gone entirely: no
              method backs it (see `useMail.ts`'s module header, "Dropped").
            */}
            <Button onClick={onToggleFlag}>⚐ Flag</Button>
            <Button variant="danger" onClick={onDelete}>
              🗑️ Delete
            </Button>
          </>
        )}
      </div>

      <div className="rbody">
        <div className="rsubject">{mail.subject}</div>

        <div className="rhead">
          <div className="avatar" style={{ background: avatarColor(sender.name) }}>
            {initials(sender.name)}
          </div>
          <div className="who">
            <div className="n">{sender.name}</div>
            <div className="e">
              &lt;{sender.email}&gt; · to me
              {sender.forwarded ? (
                <>
                  {' · '}
                  <span title="This mailbox's own address; the mail was forwarded here.">
                    fwd by {mail.from_name}
                  </span>
                </>
              ) : null}
              {enquiryId ? (
                <>
                  {' · '}
                  <span className="linkish" onClick={() => onOpenEnquiry(enquiryId)}>
                    {enquiryId}
                  </span>
                </>
              ) : null}
              {mail.flagged ? (
                <>
                  {' · '}
                  <Badge tone="warn">⚑ Flagged</Badge>
                </>
              ) : null}
            </div>
          </div>
          <div className="tm">
            {formatDate(mail.received_at)} {formatTime(mail.received_at)}
          </div>
        </div>

        {isInbound ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 8px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700 }}>
              {mail.attachments.length} attachment(s) — preview, or start an RFQ from a file
            </div>
            {/*
              "⬆ Upload file → RFQ" was here and was REMOVED at the user's request.

              It uploaded a local file and ran extraction over it in one step, for the case where
              the customer's list arrives separately from the mail that mentions it. Worth knowing
              if it is ever reinstated: it was the only caller of `useUploadFile`, which is still
              mock-only — `upload_file` is callable on the real server but no captured response
              documents its shape, so nothing here ever produced a real Frappe File docname. The
              button therefore could not work against live data in any case.

              Its prop and `MailPage`'s `handleUploadAndStartRfq` were removed with it.
            */}
          </div>
        ) : null}

        {mail.attachments.length > 0 ? (
          <div className="attgrid">
            {mail.attachments.map((attachment) => (
              <AttachmentCard
                key={attachment.id}
                attachment={attachment}
                onPreview={() => onPreviewAttachment(attachment)}
              />
            ))}
          </div>
        ) : null}

        {/*
          Rendered as TEXT, not markup. A mail body is attacker-controlled — anyone can send
          us one — so injecting it with dangerouslySetInnerHTML would be a stored-XSS hole in
          a screen whose whole job is opening untrusted mail. The prototype uses innerHTML;
          that is not safe here.

          To show real formatted mail, the BACKEND must sanitise (strip scripts, event
          handlers and external refs) and the client should render the result in a sandboxed
          iframe. Until then `white-space: pre-wrap` on `.rtext` keeps plain bodies readable.
        */}
        <div className="rtext">{htmlToText(mail.body_html)}</div>

        {/*
          The confirmation bar sits AFTER the body on purpose: it guesses the customer and delivery
          location straight off this mail, so checking the guess means reading the mail alongside
          it — the customer's name out of the signature, their port out of a sentence halfway down.
          A modal or a separate page hides exactly the thing you need to confirm against. See
          `CreateEnquiryBar.tsx`'s own header for why this replaced a whole form. Inbound mail only;
          there is nothing to raise an enquiry from on a sent item or a draft.

          Briefly moved above the attachments on 2026-09-04 and moved back at Akhil's request: on a
          mail with several attachments it is a scroll away, and that is the accepted trade for
          confirming the guess against the mail it was read from.
        */}
        {isInbound ? <ClassificationBar mail={mail} /> : null}
        {/* `key` is load-bearing: without it the bar keeps mail A's extraction, customer and lines
            when you click to a CACHED mail B (no unmount, because `useMailMessage` resolves from
            cache with isPending false), and Create then writes B's mail_id with A's contents. */}
        {/*
          A supplier answering one of OUR RFQs is not something to raise an enquiry FROM — doing so
          creates a second enquiry for work already in flight. Suppressed rather than merely
          discouraged, because the classifier scores a reply high on exactly the keywords a genuine
          request uses. `readsAsSupplierReply` honours the user's own correction in both directions.
        */}
        {isInbound && !readsAsSupplierReply(mail) ? (
          <CreateEnquiryBar key={mail.id} mail={mail} alreadyProcessed={alreadyProcessed} />
        ) : null}
      </div>
    </div>
  )
}
